/**
 * Shared modal building blocks for the media-studio dialogs
 * (VisualStudioModal, ReelStudioModal, CarouselStudioModal).
 *
 * Why a separate file: each studio modal needs the same chrome (overlay, sticky
 * header/footer, "generating…" indicator) but different bodies. Instead of a
 * monolithic <BaseModal>, expose the individual pieces so each studio composes
 * its own layout while inheriting consistent styling and keyboard handling.
 *
 *   <ModalOverlay onClose={…}>
 *     <ModalHeader icon={…} title="Visual Studio" subtitle="…" onClose={…} />
 *     <div className="p-6 space-y-5"> … body … </div>
 *     <ModalFooter> … buttons … </ModalFooter>
 *   </ModalOverlay>
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

// ---------------------------------------------------------------------------
// ModalOverlay — fixed backdrop, Escape-to-close, click-outside-to-close
// ---------------------------------------------------------------------------

export function ModalOverlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  // Close on Escape — bound at the window so it works regardless of focus.
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onClose]);

  // Lock body scroll while the modal is open so the page underneath
  // doesn't scroll the modal out of view.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // CRITICAL: portal to <body>. The studio modals are rendered as children
  // of `mk-card-interactive`, which adds a `transform: translateY(-2px)` on
  // hover. The CSS spec says any non-`none` transform on an ancestor turns
  // that element into the containing block for `position: fixed` descendants
  // — so without a portal the modal would re-anchor to the card on hover and
  // jump around / flicker. Mounting at body bypasses every ancestor's
  // transform / filter / contain rules.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
      onClick={(e) => {
        // Only close when the user clicks the backdrop, not the dialog itself.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// ModalDialog — the inner card. Wrap children to get the right chrome,
// max-height, and scroll behaviour. Width is opt-in via maxWidthClass so
// each studio can pick its own (xl for visual/reel, 2xl for carousel).
// ---------------------------------------------------------------------------

export function ModalDialog({
  children,
  maxWidthClass = 'max-w-xl',
}: {
  children: ReactNode;
  maxWidthClass?: string;
}) {
  return (
    <div
      className={`mk-card flex w-full ${maxWidthClass} max-h-[92vh] flex-col overflow-hidden rounded-2xl shadow-2xl shadow-black/30`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModalHeader — flame icon tile + title/subtitle + close button
// ---------------------------------------------------------------------------

export function ModalHeader({
  icon,
  title,
  subtitle,
  onClose,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="border-border bg-card sticky top-0 z-10 flex items-center justify-between border-b px-6 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="bg-accent/15 text-accent flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl">
          {icon}
        </div>
        <div className="min-w-0">
          <h2 className="font-heading text-text-primary truncate text-base font-bold">{title}</h2>
          {subtitle && <p className="text-text-tertiary truncate text-xs">{subtitle}</p>}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('common.close')}
        className="text-text-tertiary hover:bg-subtle hover:text-text-primary flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ModalBody — scrollable content area (used between Header and Footer)
// ---------------------------------------------------------------------------

export function ModalBody({ children }: { children: ReactNode }) {
  return <div className="flex-1 overflow-y-auto p-6">{children}</div>;
}

// ---------------------------------------------------------------------------
// ModalFooter — sticky bottom bar for the action buttons
// ---------------------------------------------------------------------------

export function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div className="border-border bg-card sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t px-6 py-4">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GeneratingIndicator — flame-tinted "in progress" hint with ETA
// ---------------------------------------------------------------------------

export function GeneratingIndicator({ label, estimate }: { label: string; estimate: string }) {
  return (
    <div className="border-accent/25 bg-accent/8 flex items-center gap-3 rounded-xl border px-4 py-3">
      <Loader2 className="text-accent h-4 w-4 flex-shrink-0 animate-spin" />
      <div className="min-w-0">
        <p className="text-accent text-sm font-medium">{label}</p>
        <p className="text-text-tertiary text-xs">{estimate}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorBox — uniform red-tinted error message used by every studio
// ---------------------------------------------------------------------------

export function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-red-500/25 bg-red-500/8 px-4 py-3 text-sm text-red-600">
      {message}
    </div>
  );
}
